import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import confetti from "canvas-confetti";
import { API, useApp } from "../context";
import { isPromptFree } from "../lib/promptFree";

// Orange autumn banner. Two things only: it's the Fall promo, and premium is
// free for everyone through the end of October 2026. Shows for EVERYONE on all
// header pages when the Fall window is live (theme === "fall"), OR in a local
// PREVIEW mode (localStorage iw_fall_preview="1") so the admin can eyeball it
// on the site WITHOUT turning free premium on for everyone. Never blocks content.

const isHiddenRoute = (path) => {
  if (path.startsWith("/arcade/") && path !== "/arcade/lobby") return true;
  if (path.startsWith("/learn/") && path !== "/learn/academy/forest") return true;
  return false;
};

const CONFETTI_ROUTES = new Set(["/", "/login", "/signup"]);
const FALL_PREVIEW_END = "2026-11-01T07:00:00+00:00";

const fmt = (ms) => {
  if (ms <= 0) return "0m";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
};

// Realistic autumn leaves drifting DOWN across the full width, tumbling like
// real leaves. Uses several DISTINCT emoji leaf shapes (maple / fallen /
// fluttering / clover) at a larger size so they clearly read as leaves, in two
// size classes for a natural look. Falls back to squares only if unsupported.
const fireLeaves = () => {
  let bigShapes, smallShapes;
  try {
    bigShapes = [
      confetti.shapeFromText({ text: "🍁", scalar: 3.4 }),
      confetti.shapeFromText({ text: "🍂", scalar: 3.4 }),
      confetti.shapeFromText({ text: "🍃", scalar: 3.4 }),
    ];
    smallShapes = [
      confetti.shapeFromText({ text: "🍁", scalar: 2.2 }),
      confetti.shapeFromText({ text: "🍂", scalar: 2.2 }),
      confetti.shapeFromText({ text: "🍃", scalar: 2.2 }),
    ];
  } catch (_) {
    bigShapes = smallShapes = ["square", "circle"];
  }
  const shoot = (count, drift, shapes, scalar) => {
    for (let i = 0; i <= 12; i++) {
      confetti({
        particleCount: count,
        startVelocity: 12,
        angle: 270,
        spread: 130,
        gravity: 0.6,       // steady fall so leaves reach the very bottom
        drift,              // and drift sideways like real leaves
        ticks: 1200,        // long enough to travel the FULL screen on any device
        origin: { x: i / 12, y: 0 },
        shapes,
        scalar,
        zIndex: 70,
        disableForReducedMotion: true,
      });
    }
  };
  shoot(2, 0.9, bigShapes, 3.4);
  setTimeout(() => shoot(3, -0.7, smallShapes, 2.2), 300);
  setTimeout(() => shoot(2, 1.2, bigShapes, 3.4), 700);
};

// Hoisted to module scope so the 1s countdown tick never changes their
// component identity. Defining these INSIDE the render body made React treat
// them as a brand-new component type every second, remounting the bouncing
// emoji spans and restarting their animation — which read as the banner
// "flashing" the whole header once per second.
const AUTUMN = ["🍁", "🎃", "🍂", "🌰", "🍃", "🍁", "🍂"];
const Leaves = () => (
  <span className="inline-flex items-center gap-1.5 sm:gap-2.5 text-base sm:text-lg leading-none shrink-0" aria-hidden="true">
    {AUTUMN.map((ic, i) => (
      <span key={i} className="inline-block animate-bounce" style={{ animationDelay: `${i * 0.12}s` }}>{ic}</span>
    ))}
  </span>
);
const MiniLeaves = () => (
  <span className="inline-flex items-center gap-1.5 text-sm leading-none shrink-0" aria-hidden="true">
    {["🍁", "🎃", "🍂", "🌰"].map((ic, i) => (
      <span key={i} className="inline-block animate-bounce" style={{ animationDelay: `${i * 0.15}s` }}>{ic}</span>
    ))}
  </span>
);

export default function FallLaunchBanner() {
  const { user } = useApp();
  // Instant render: hydrate from the last-known promo state cached in
  // localStorage so the banner paints WITH the page on refresh (no 3–5s gap
  // waiting for the /premium-preview/active fetch). We revalidate in the
  // background and update the cache.
  const [win, setWin] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("iw_fall_win") || "null");
      return c && c.active && c.theme === "fall" && c.ends_at && new Date(c.ends_at).getTime() > Date.now() ? c : null;
    } catch { return null; }
  });
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();
  const location = useLocation();
  const confettiFiredRef = useRef(false);

  // Local preview: admin flips this on to see the banner without going live.
  const previewMode = (() => {
    try {
      if (new URLSearchParams(window.location.search).get("fallpreview") === "1") {
        localStorage.setItem("iw_fall_preview", "1");
      }
      return localStorage.getItem("iw_fall_preview") === "1";
    } catch { return false; }
  })();

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/premium-preview/active`);
      const w = r.data?.active && r.data?.theme === "fall" ? r.data : null;
      setWin(w);
      try {
        if (w) localStorage.setItem("iw_fall_win", JSON.stringify(w));
        else localStorage.removeItem("iw_fall_win");
      } catch { /* storage unavailable */ }
    } catch (_) {
      // keep the cached value on a transient failure (don't blank the banner)
    }
  }, []);

  useEffect(() => {
    poll();
    const p = setInterval(poll, 60000);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(p); clearInterval(t); };
  }, [poll]);

  const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
  const onArrival = CONFETTI_ROUTES.has(path);
  const liveActive = !!win?.active && !!win.ends_at && new Date(win.ends_at).getTime() > now;
  const active = liveActive || previewMode;
  const endsAt = win?.ends_at || FALL_PREVIEW_END;

  useEffect(() => {
    if (active && onArrival && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      fireLeaves();
      const again = setInterval(fireLeaves, 50000);
      return () => clearInterval(again);
    }
    if (!onArrival || !active) confettiFiredRef.current = false;
  }, [active, onArrival]);

  if (isHiddenRoute(path)) return null;
  if (!active) return null;
  const remaining = new Date(endsAt).getTime() - now;

  // During the free-premium window there's nothing to upgrade to — the banner
  // stays (countdown + "free premium" message) but the upsell tap is a no-op.
  const onKeepIt = () => { if (isPromptFree()) return; if (user?.id) navigate("/subscribe"); };

  return (
    <div
      data-testid="fall-launch-banner"
      className="w-full bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 text-white shadow overflow-hidden"
    >
      {previewMode && !liveActive && (
        <div className="bg-black/30 text-center text-[10px] font-bold py-0.5 tracking-wide" data-testid="fall-preview-flag">
          👁 PREVIEW ONLY — not live for users yet
        </div>
      )}
      {/* Mobile: compact two-line layout */}
      <div
        role="button" tabIndex={0}
        onClick={onKeepIt}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onKeepIt(); }}
        data-testid="fall-banner-mobile"
        className="lg:hidden px-3 py-1.5 flex flex-col items-center justify-center gap-0.5 text-center cursor-pointer active:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-1.5">
          <MiniLeaves />
          <span className="font-extrabold text-[12px] leading-none whitespace-nowrap">🍂 Fall Free Premium!</span>
          <MiniLeaves />
        </div>
        <div className="flex items-center justify-center gap-1.5 leading-none">
          <span className="text-[10px] font-bold uppercase tracking-wide text-yellow-200 whitespace-nowrap">Free through October</span>
          <span className="text-orange-100/70 text-[10px]">•</span>
          <span data-testid="fall-countdown-mobile" className="tabular-nums bg-white/20 rounded px-1.5 py-0.5 text-[10px] font-extrabold text-white whitespace-nowrap">
            {fmt(remaining)} left
          </span>
        </div>
      </div>

      {/* Wide screens only: full single-row layout */}
      <div className="hidden lg:flex items-center justify-center gap-3 py-1.5 px-3 text-sm font-bold">
        <Leaves />
        <span className="flex flex-col items-center leading-none gap-0.5">
          <span className="font-extrabold whitespace-nowrap">🍂 Fall at Isaiah's World</span>
          <span className="text-[11px] font-bold text-orange-100 tracking-wide whitespace-nowrap">Cozy season, wide-open doors</span>
        </span>
        <span data-testid="fall-free-chip" className="inline-block bg-yellow-300 text-orange-900 font-black uppercase tracking-wide text-xs rounded-full px-2.5 py-0.5 shadow-sm whitespace-nowrap shrink-0">
          🍁 Free Premium Access
        </span>
        <span className="font-semibold text-orange-50 whitespace-nowrap hidden md:inline">
          All premium tools free until <span className="text-white font-extrabold">November 1</span>
        </span>
        <span data-testid="fall-countdown" className="tabular-nums bg-white/20 rounded-md px-2 py-0.5 text-white font-extrabold whitespace-nowrap shrink-0">
          {fmt(remaining)} left
        </span>
        {!isPromptFree() && (
        <button type="button" onClick={onKeepIt} data-testid="fall-keep-it"
          className="inline-flex items-center gap-0.5 bg-white/20 hover:bg-white/30 transition-colors rounded-full px-3 py-1 shrink-0">
          Keep it <ChevronRight size={14} />
        </button>
        )}
        <Leaves />
      </div>
    </div>
  );
}
